// ===============================================
// 🔧 CONFIGURACIÓN API CORREGIDA - DESARROLLO Y PRODUCCIÓN
// ===============================================
// REEMPLAZAR tu archivo apiConfig.ts

// 🎯 DETECCIÓN AUTOMÁTICA DE ENTORNO
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// 🌐 CONFIGURACIÓN DINÁMICA SEGÚN ENTORNO
const getBaseUrl = (): string => {
  // En desarrollo: conexión directa al backend
  if (isDevelopment) {
    return import.meta.env.VITE_API_URL || 'http://localhost:3001';
  }
  
  // En producción: usar proxy Nginx (rutas relativas)
  if (isProduction) {
    return ''; // Rutas relativas para proxy
  }
  
  // Fallback
  return 'http://localhost:4343';
};

// 🔧 CONFIGURACIÓN PRINCIPAL
export const API_BASE_URL = isDevelopment 
  ? `${getBaseUrl()}`  // Desarrollo: http://localhost:4343/api
  : '/api';                // Producción: /api (proxy)

export const WS_BASE_URL = isDevelopment
  ? `${getBaseUrl()}/socket.io`  // Desarrollo: http://localhost:4343/socket.io
  : '/socket.io';               // Producción: /socket.io (proxy)

// 📋 CONFIGURACIÓN DETALLADA
export const apiConfig = {
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Agregar configuración para CORS en desarrollo
  withCredentials: isDevelopment,
};

// 🌍 CONFIGURACIÓN POR ENTORNO
export const BASE_URL = getBaseUrl();

// 🔧 FUNCIÓN HELPER MEJORADA
export const buildApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const finalUrl = `${API_BASE_URL}${cleanEndpoint}`;
  
  // 🔍 DEBUG: Ver qué se está construyendo
  console.log('🔍 [buildApiUrl] Input:', endpoint);
  console.log('🔍 [buildApiUrl] API_BASE_URL:', API_BASE_URL);
  console.log('🔍 [buildApiUrl] Final URL:', finalUrl);
  
  return finalUrl;
};

// 🐛 DEBUG INFO
console.log('🔧 [API-CONFIG] Configuración cargada:', {
  environment: import.meta.env.MODE,
  isDevelopment,
  isProduction,
  baseUrl: BASE_URL,
  apiBaseUrl: API_BASE_URL,
  wsBaseUrl: WS_BASE_URL
});

// CONFIGURACION DE UPLOADS
// En desarrollo: backend directo (puerto 3001)
// En produccion: Nginx proxy (puerto 4343 o 443)
export const UPLOADS_URL = '/uploads';                      // Produccion: relativa (Nginx)

// 🔧 FUNCIÓN HELPER PARA UPLOADS
export const buildUploadUrl = (folder: string, filename: string): string => {
  // Limpiar inputs
  const cleanFolder = folder.replace(/^\/+|\/+$/g, '');
  const cleanFilename = filename.replace(/^\/+/g, '');
  
  // Construir path
  const finalUrl = `${UPLOADS_URL}/${cleanFolder}/${cleanFilename}`;
  
  // Logging solo en desarrollo
  if (isDevelopment) {
    console.log('📎 [buildUploadUrl]', {
      folder,
      filename,
      base: UPLOADS_URL,
      output: finalUrl
    });
  }
  
  return finalUrl;
};

// 📋 ENDPOINTS ESPECIFICOS
export const API_ENDPOINTS = {
  // Autenticacion
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  
  // Chat
  CHAT_SESSION: '/chats/session',
  CHAT_USER_GROUPED: (userId: string) => `/chats/user/${userId}/grouped`,
  CHAT_MESSAGES: (sessionId: string) => `/chats/${sessionId}/messages`,
  CHAT_SPECIFIC: (chatId: string) => `/chats/${chatId}`,
  
  // Busqueda y documentos
  SEARCH: '/search',
  TECHNICAL_ASK: '/ask',
  
  // IA Y ANALISIS
  AI_ANALYZE_DESCRIPTION: '/ai/analyze-description',
  
  // Tickets
  TICKETS: '/tickets',
  TICKET_BY_ID: (id: string) => `/tickets/${id}`,
  TICKET_CREATE: '/tickets',
  TICKET_SCORE: (id: string) => `/tickets/${id}/score`,
  TICKET_QUOTATION: (id: string) => `/tickets/${id}/quotation`,
  TICKET_QUOTATION_DOWNLOAD: (id: string) => `/tickets/${id}/quotation/download`,
  TICKET_QUOTATION_REGENERATE: (id: string) => `/tickets/${id}/quotation/regenerate`,
  
  // Sistema y salud
  HEALTH: '/health',
  CONFIG: '/config',
  STATUS: '/status',
  
  // Admin Dashboard
  ANALYTICS_SEARCHES: '/analytics/searches',
  ANALYTICS_POPULAR: '/analytics/popular',
  ADMIN_EQUIPMENT: '/admin/equipment',
  DEBUG_DASHBOARD: '/debug/dashboard',
  CONFIG_STATS: '/config/stats'
};

// 🔧 FUNCION PARA CONSTRUIR URL CON PARAMETROS
export const buildApiUrlWithParams = (endpoint: string, params?: Record<string, string>): string => {
  let url = buildApiUrl(endpoint);
  
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  
  return url;
};

// 🔧 FUNCION HELPER PARA FETCH
export const apiRequest = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const url = buildApiUrl(endpoint);
  
  const defaultOptions: RequestInit = {
    headers: {
      ...apiConfig.headers,
      ...options.headers,
    },
    credentials: isDevelopment ? 'include' : 'same-origin',
    ...options,
  };

  console.log(`[API] ${options.method || 'GET'} ${url}`);
  
  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      console.error(`[API] Error ${response.status}: ${response.statusText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error(`[API] Request failed:`, error);
    throw error;
  }
};

// 🔧 FUNCION HELPER PARA GET
export const apiGet = async (endpoint: string): Promise<any> => {
  const response = await apiRequest(endpoint, { method: 'GET' });
  return response.json();
};

// 🔧 FUNCION HELPER PARA POST
export const apiPost = async (endpoint: string, data: any): Promise<any> => {
  const response = await apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

// ✅ EXPORTACIÓN PARA COMPATIBILIDAD
export default {
  API_BASE_URL,
  WS_BASE_URL,
  apiConfig,
  BASE_URL,
  UPLOADS_URL,
  API_ENDPOINTS,
  buildApiUrl,
  buildUploadUrl,
  buildApiUrlWithParams,
  apiRequest,
  apiGet,
  apiPost
};